import { getEnvConfig } from '@/config/env';
import { useState, useCallback, useEffect } from 'react';
import { Modal, Radio, Button, useToast } from '@chikara/ui';
import { formatCurrency } from '@chikara/utils';
import styles from './ReportChargebackModal.module.css';
import { t } from '@/i18n';

interface ReportChargebackModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: any;
}

export function ReportChargebackModal({ isOpen, onClose, transaction }: ReportChargebackModalProps) {
  const toast = useToast();
  const [selectedMotive, setSelectedMotive] = useState<string>('');
  const [hasCopied, setHasCopied] = useState(false);
  const { zendeskUrl } = getEnvConfig();
  
  useEffect(() => {
    if (!isOpen) {
      setSelectedMotive('');
      setHasCopied(false);
    }
  }, [isOpen]);

  const handleCopy = useCallback(() => {
    if (!transaction) return;

    const formattedDate = transaction.cdate;
    const amount = formatCurrency(transaction.amount, { locale: 'es-MX', currency: 'MXN' });

    const textToCopy = `${t('dashboard.reportModal.details.date')}: ${formattedDate}` +
      `\n${t('dashboard.reportModal.details.description')}: ${transaction.movement?.description || ''}` +
      `\n${t('dashboard.reportModal.details.merchant')}: ${transaction.narrative || ''}` +
      `\n${t('dashboard.reportModal.details.amount')}: ${amount}` +
      `\n${t('dashboard.reportModal.details.traceId')}: ${transaction.tracking_key || ''}` +
      `\n${t('dashboard.reportModal.motivesTitle')}: ${t(`dashboard.reportModal.motives.${selectedMotive}`)}`;

    navigator.clipboard.writeText(textToCopy).then(() => {
      setHasCopied(true);
      toast.success(t('dashboard.reportModal.successCopy'));
    });
  }, [transaction, selectedMotive, toast]);

  const handleContinue = () => {
    window.open(zendeskUrl, '_blank', 'noopener,noreferrer');
    onClose();
  };

  if (!transaction) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('dashboard.reportModal.title')}
      description={t('dashboard.reportModal.subtitle')}
      actionLabel={t('dashboard.reportModal.actions.continue')}
      onAction={handleContinue}
      isActionDisabled={!hasCopied || !selectedMotive}
      contentSize="lg"
    >
      <div className={styles.container}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('dashboard.reportModal.motivesTitle')}</h3>
          <div className={styles.radioGroup}>
            {Object.keys(t('dashboard.reportModal.motives')).map((key) => (
              <Radio
                key={key}
                name="motive"
                label={t(`dashboard.reportModal.motives.${key}`)}
                checked={selectedMotive === key}
                onChange={() => {
                  setSelectedMotive(key);
                  setHasCopied(false); // Reset copy state if motive changes
                }}
              />
            ))}
          </div>
        </div>

        <div className={styles.detailsSection}>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{t('dashboard.reportModal.details.date')}</span>
            <span className={styles.detailValue}>{transaction.cdate}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{t('dashboard.reportModal.details.description')}</span>
            <span className={styles.detailValue}>{transaction.movement?.description}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{t('dashboard.reportModal.details.merchant')}</span>
            <span className={styles.detailValue}>{transaction.narrative || 'N/A'}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{t('dashboard.reportModal.details.amount')}</span>
            <span className={styles.detailValue}>{formatCurrency(transaction.amount, { locale: 'es-MX', currency: 'MXN' })}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{t('dashboard.reportModal.details.traceId')}</span>
            <span className={styles.detailValue}>{transaction.tracking_key || 'N/A'}</span>
          </div>
        </div>

        <p className={styles.promptText}>{t('dashboard.reportModal.copyPrompt')}</p>

        <Button
          variant="outline"
          onClick={handleCopy}
          fullWidth
          className={styles.copyButton}
          disabled={!selectedMotive}
        >
          <span className={styles.buttonContent}>
            {t('dashboard.reportModal.actions.copy')}
          </span>
        </Button>
      </div>
    </Modal>
  );
}
